import {ID_PREFIX} from "../utils/constants";
import {debug, error, info, warn} from "../utils/log";
import {validate} from "../pow/validate";
import {isErrorRetryable, transformToTranslatableError} from "../utils/error";
import {getChecksum} from "./context-shared";
import {later, neverResolve} from "../utils/basic";
import type {IStateStorage} from "./state";
import {type IpcMessageChecksum, isIpcMessage, isResendableMessage, type ResendableIpcMessage} from "./ipc-shared";

// We could do this in a SharedWorker, but for now we have to use this work-around since SharedWorkers are not supported in all relevant browsers

export function leader(leaderData: IStateStorage, openLeaderRequests: ResendableIpcMessage[] , updateLeaderStatus:(newStatus: boolean) => void): void {
    let currentValidationController: AbortController | null = null;
    const abortReason = Symbol('new validation started');
    let channel: BroadcastChannel|undefined = undefined;
    let checksumId: number|undefined = undefined;
    navigator.locks.request(`${ID_PREFIX}leader`, {}, () => {
        info("This browser tab is the leader. It is responsible for PoW validation and solving.");
        updateLeaderStatus(true);
        let leaderStateCounter = 0
        const dumpState = () => {
            debug("Leader state dump:", leaderData);
            channel?.postMessage({
                type: 'state-dump',
                id: crypto.randomUUID(),
                state: leaderData
            })
        }
        if (leaderData.state === 'initializing') { // we might become the leader after the initial state is set (another tab closed while we were open)
            debug("Receiver state is initializing, applying initial state changes.");
            let solution = '';
            try {
                solution = localStorage.getItem(`${ID_PREFIX}solution`) || '';
                leaderData.solution = solution;
                debug("Loaded solution from localStorage:", solution);
            } catch (err) {
                warn("Failed to load solution from localStorage:", err);
            }
        }
        // after we become the leader, let's check if our solution is valid
        openLeaderRequests.push({type: 'request-validation', id: ''})
        channel = new BroadcastChannel(`${ID_PREFIX}ipc`);

        let droppedValidationRequest = false;
        let hiddenValidation = false
        function doValidation() {
            droppedValidationRequest = false
            debug("Starting validation process for solution:", leaderData.solution);
            if (leaderData.state === 'error' && !isErrorRetryable(leaderData.errorId)) {
                debug("In error state that is not retryable, aborting.");
                return;
            }
            debug("Starting validation process for solution:", leaderData.solution);
            // if we are in "solved" state skip propagating the "validating" state - we most likely have a valid solution.
            // prevents flickering of the UI in case the solution is valid.
            if (leaderData.state !== 'solved') {
                leaderData.state = 'validating';
                leaderData.rate = 0
                leaderData.errorId = '';
                leaderData.errorMsg = '';
                leaderStateCounter++
                dumpState()
            } else {
                hiddenValidation = true
            }
            if (currentValidationController) {
                currentValidationController.abort(abortReason);
            }
            currentValidationController = new AbortController();
            validate(leaderData.solution, currentValidationController.signal, (up) => {
                if (leaderData.state !== 'solving') {
                    leaderData.state = 'solving';
                    try {
                        localStorage.removeItem(`${ID_PREFIX}solution`);
                        debug("Removed solution from localStorage.");
                    } catch (e) {
                        warn("Failed to remove solution from localStorage:", e);
                    }
                }
                leaderData.rate = up.rate;
                leaderStateCounter++
                dumpState();
                hiddenValidation = false
            }).then(newSolution => {
                debug("Validation completed, new solution:", newSolution);
                currentValidationController = null;
                leaderData.state = 'solved';
                leaderData.solution = newSolution;
                leaderStateCounter++
                // Persist the solution to localStorage
                try {
                    localStorage.setItem(`${ID_PREFIX}solution`, newSolution);
                    debug("Persisted solution to localStorage.");
                } catch (e) {
                    warn("Failed to persist solution to localStorage:", e);
                }
                dumpState()
            }).catch((err) => {
                if (err === abortReason) {
                    debug("Validation aborted due to new validation request.");
                    return;
                }
                currentValidationController = null;
                try {
                    localStorage.removeItem(`${ID_PREFIX}solution`);
                    debug("Removed solution from localStorage.");
                } catch (e) {
                    warn("Failed to remove solution from localStorage:", e);
                }
                leaderData.state = 'error';
                const transErr = transformToTranslatableError(err)
                leaderData.errorId = transErr.id;
                leaderData.errorMsg = transErr.msg;
                leaderStateCounter++
                debug("Validation error:", err);
                dumpState();
            }).finally(() => {
                hiddenValidation = false
                if (droppedValidationRequest) {
                    debug("While we were validating, at least one new validation request came in. We will re-queue a new validation request.");
                    droppedValidationRequest = false
                    setTimeout(() => {
                        if (!droppedValidationRequest && leaderData.state !== 'validating' && leaderData.state !== 'solving') {
                            doValidation()
                        }
                    }, 500)
                }
            })
        }
        function processMessage(data: unknown) {
            try {
                if (!isIpcMessage(data)) {
                    warn("Received non-IPC message:", data);
                    return;
                }
                // we are only interested in these two message types
                if (!['request-validation', 'request-dump'].includes(data.type)) {
                    return;
                }
                debug("leader is processing IPC message:", data);
                if (data.type === 'request-validation') {
                    if (leaderData.state === 'initializing' || leaderData.state === 'error' || (leaderData.state === 'solved' && !hiddenValidation)) {
                        doValidation();
                    } else if (leaderData.state === 'validating' || leaderData.state === 'solving' || (leaderData.state === 'solved' && hiddenValidation)) {
                        debug(`Received validation request while we are in state ${leaderData.state}${hiddenValidation ? ' (hidden validation)':''}, ignore.`);
                        droppedValidationRequest = true
                    }
                }
                if (isResendableMessage(data)) {
                    channel?.postMessage({
                        type: 'ack',
                        id: data.id
                    })
                }
                dumpState()
            } catch (err) {
                error("Failed to handle IPC message:", data, err);
            }
        }

        channel.onmessage = (event) => processMessage(event.data);
        // process any open requests (we observed as follower at least) immediately
        openLeaderRequests.forEach(req => processMessage(req));

        // Send a checksum every 60 seconds to ensure state consistency across tabs.
        // Normally no message gets lost, so the data should always be in sync, but this is a safety measure.
        // That's why we have chosen a relatively long interval to avoid flooding the channel with messages.
        checksumId = window.setInterval(async () => {
            let c
            let checksum: Uint32Array | undefined;
            let retries = 0
            while (retries < 10) {
                c = leaderStateCounter
                checksum = await getChecksum(leaderData);
                if (c === leaderStateCounter) {
                    break; // No changes, exit the loop
                }
                retries++;
                await later(1); // Small delay to let state stabilize
            }
            if (checksum) {
                channel?.postMessage({
                    type: 'checksum',
                    id: crypto.randomUUID(),
                    checksum: checksum
                } as IpcMessageChecksum)
            } else {
                warn("Failed to get checksum, retrying in 5 seconds.");
            }
        }, 60 * 1000); // 60 seconds

        // we hold the lock, so only ever one tab is responsible to persist the state
        // if this tab is closed, the lock will be released, and another tab will take over
        return neverResolve()
    }).finally(() => {
        warn("Leader lock was stolen, no longer the leader.");
        if (channel) {
            channel.close();
            channel = undefined
        }
        if (checksumId) {
            clearInterval(checksumId);
            checksumId = undefined;
        }
        updateLeaderStatus(false);
        if (currentValidationController) {
            currentValidationController.abort(abortReason);
            currentValidationController = null;
        }
    })
}
